import React, { useState, useRef, useEffect } from "react";
import { useFormik, FormikProps } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import { useNavigate, useParams } from "react-router-dom";
import { sendTriggerEmails } from "utils/emailService";
import { Office } from "types";
import { showSuccess, showError } from "toast/toast";
import { assignedOfficesValidation, emailValidation, firstNameValidation, jobTitleValidation, lastNameValidation, passwordValidation, permissionsValidation, phoneValidation, statusValidation } from "validationSchemas/common";
import { getAuthCookie } from "../../utils/authCookies";

interface PasswordInputProps {
  formik: FormikProps<{ password: string }>;
}

type SelectOption = {
  value: string;
  label: string;
};

const SignageBrokerageAddTeammember = () => {
  const { firmId } = useParams<{ firmId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [formSubmitting, setFormSubmitting] = useState(false);
  const token = getAuthCookie("sessionToken");
  const navigate = useNavigate();
  const [offices, setOffices] = useState<Office[] | null>(null);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [validation, setValidation] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    digit: false,
    specialChar: false,
  });

  const togglePasswordVisibility = () => {
    setPasswordVisible((prevVisibility) => !prevVisibility);
  };

  const validatePassword = (password: string) => {
    setValidation({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      digit: /\d/.test(password),
      specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    formik.handleChange(e);
    validatePassword(value);
  };

  const [showValidationBox, setShowValidationBox] = useState(false);

  const handleFocus = () => {
    setShowValidationBox(true);
  };

  const handleBlur = () => {
    setShowValidationBox(false);
  };

  const permissionsOptions: SelectOption[] = [
    { value: "all", label: "All" },
    { value: "view", label: "View" },
    { value: "edit", label: "Edit" },
    { value: "delete", label: "Delete" },
  ];

  useEffect(() => {
    const fetchOffices = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/offices-search?created_by.id=${firmId}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        const data = await response.json();
        setOffices(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchOffices();
  }, [token]);

  const officesOptions =
    offices?.map((office) => ({
      value: office._id,
      label: office.officeName,
    })) || [];


  const formik = useFormik({
    initialValues: {
      image: null as File | null,
      firstName: "",
      lastName: "",
      jobTitle: "",
      phone: "",
      email: "",
      assignedOffices: [] as string[], // initialized as array for React Select
      permissions: [] as string[], // initialized as array for React Select
      password: "",
      statuss: "",
    },
    validationSchema: Yup.object({
      firstName: firstNameValidation,
      lastName: lastNameValidation,
      jobTitle: jobTitleValidation,
      phone: phoneValidation,
      email: emailValidation,
      assignedOffices: assignedOfficesValidation,
      permissions: permissionsValidation,
      password: passwordValidation,
      statuss: statusValidation,
    }),
    onSubmit: async (values) => {
      console.log("Submitting form...", values);
      setFormSubmitting(true);

      let imageUrl = null;

      // Prepare assigned offices
      const assignedOffices = values.assignedOffices.map((oid) => {
        const office = offices?.find((o) => o._id === oid);
        return {
          _id: office?._id,
          officeName: office?.officeName || "",
          officeType: office?.officeType || "",
          officeAddress: {
            _id: office?.officeAddress?.[0]?._id,
            label: office?.officeAddress?.[0]?.label || "",
            addressLine1: office?.officeAddress?.[0]?.addressLine1 || "",
            addressLine2: office?.officeAddress?.[0]?.addressLine2 || "",
            city: office?.officeAddress?.[0]?.city || "",
            state: office?.officeAddress?.[0]?.state || "",
            country: office?.officeAddress?.[0]?.country || "",
            zip: office?.officeAddress?.[0]?.zip || "",
            phone: office?.officeAddress?.[0]?.phone || "",
          },
        };
      });

      try {
        if (values.image) {
          imageUrl = await uploadFile(values.image);
        }

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/user/create`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              email: values.email,
              password: values.password,
              userType: "team_member",
              firstName: values.firstName,
              lastName: values.lastName,
              jobTitle: values.jobTitle,
              phone1: values.phone,
              licenseType: "BROKERAGE_TEAM_MEMBER",
              status: values.statuss,
              profilePic: imageUrl,
              offices: assignedOffices,
              accessLevel: values.permissions,
              LicenseActive: true,
              created_by: {
                id: firmId,
                name: "Brokerage Firm",
              },
            }),
          }
        );
        const data: any = await response.json();
        if (!response.ok || data?.error) {
          const message = data?.error || data?.message || "Failed to save data";
          console.error("API Error:", message);
          showError(message);
          throw new Error(message);
        }
        showSuccess("Teammember added successfully!");
        navigate(`/signage/brokerageFirm/${firmId}`);

      } catch (err) {
        console.error("Caught error:", err);
      } finally {
        // ✅ Always run cleanup
        setFormSubmitting(false);
        setLoading(false);
      }
    }

    // onSubmit: async (values) => {
    //   console.log("Submitting form...", values);
    //   setFormSubmitting(true);

    //   let imageUrl = null;
    //   const assignedOfficeIds = new Array();
    //   for (const item of values.assignedOffices) {
    //     assignedOfficeIds.push(item);
    //   }
    //   // console.log(assignedOfficeIds);
    //   // const assignedOfficeIds =
    //   // values.assignedOffices?.map((id) => ({
    //   //   id
    //   // })) || [];
    //   // const assignedOfficeIds = values.assignedOffices as string[];
    //   const assignedOffices = new Array();
    //   for (const oid of assignedOfficeIds) {
    //     const assignedOffice = offices?.find((office) => office._id === oid);
    //     assignedOffices.push({
    //       _id: assignedOffice?._id,
    //       officeName: assignedOffice?.officeName || "",
    //       officeType: assignedOffice?.officeType || "",
    //       officeAddress: {
    //         _id: assignedOffice?.officeAddress?.[0]?._id,
    //         label: assignedOffice?.officeAddress?.[0]?.label || "",
    //         addressLine1: assignedOffice?.officeAddress?.[0]?.addressLine1 || "",
    //         addressLine2: assignedOffice?.officeAddress?.[0]?.addressLine2 || "",
    //         city: assignedOffice?.officeAddress?.[0]?.city || "",
    //         state: assignedOffice?.officeAddress?.[0]?.state || "",
    //         country: assignedOffice?.officeAddress?.[0]?.country || "",
    //         zip: assignedOffice?.officeAddress?.[0]?.zip || "",
    //         phone: assignedOffice?.officeAddress?.[0]?.phone || "",
    //       }
    //     });
    //   }

    //   try {
    //     if (values.image) {
    //       imageUrl = await uploadFile(values.image);
    //     }

    //     const response = await fetch(
    //       `${process.env.REACT_APP_API_URL}/user/create`,
    //       {
    //         method: "POST",
    //         headers: {
    //           "Content-Type": "application/json",
    //           Authorization: `Bearer ${token}`,
    //         },
    //         body: JSON.stringify({
    //           email: values.email,
    //           password: values.password,
    //           userType: "team_member",
    //           firstName: values.firstName,
    //           lastName: values.lastName,
    //           jobTitle: values.jobTitle,
    //           phone1: values.phone,
    //           licenseType: "BROKERAGE_TEAM_MEMBER",
    //           status: values.statuss,
    //           profilePic: imageUrl,
    //           offices: assignedOffices,
    //           accessLevel: values.permissions,
    //           LicenseActive: true,
    //           created_by: {
    //             id: firmId,
    //             name: "Brokerage Firm",
    //           },
    //         }),
    //       }
    //     );

    //     if (!response.ok) {
    //       throw new Error("Failed to save data");
    //     }

    //     const result = await response.json();
    //     // toast.success("Teammember added successfully!");
    //     // console.log("Form submission successful:", result);
    //     // navigate(`/signage/brokerageFirm/${firmId}`, {
    //     //   state: { successMessage: "Teammember added successfully!" },
    //     // });

    //      const created_id = result.id;
    //         console.log("Form submission successful:", created_id);
    //        if (created_id) {
    //       setLoading(true);

    //       const hierarchyRes = await fetch(
    //         `${process.env.REACT_APP_API_URL}/users/hierarchy/by-email/${values.email}`,
    //         {
    //           method: "GET",
    //           headers: {
    //             Authorization: `Bearer ${token}`,
    //             "Content-Type": "application/json",
    //           },
    //         }
    //       );

    //       if (!hierarchyRes.ok) {
    //         throw new Error("Failed to fetch hierarchy");
    //       }

    //       const hierarchyData = await hierarchyRes.json();
    //       console.log("warehousesusers/hierarchy/{id}:", hierarchyData);

    //       // Extract emails
    //       const dynamicRecipients = hierarchyData.hierarchy
    //         .map((user: any) => user.email)
    //         .filter((email: string | undefined) => !!email);

    //       // Extract licenseTypes
    //       const licenseTypes = hierarchyData.hierarchy
    //         .map((user: any) => user.licenseType)
    //         .filter((licenseType: string | undefined) => !!licenseType);

    //       // Extract user names
    //       const allUserNames = hierarchyData.hierarchy
    //         .map((user: any) => user.name)
    //         .filter((name: string | undefined) => !!name);

    //       console.log("dynamicRecipients:", dynamicRecipients);
    //       console.log("allUserNames:", allUserNames);

    //       const params = {
    //         userName: `${values.firstName} ${values.lastName}`,
    //         licenseId: "TEST-123",
    //         allUserNames,
    //       };

    //       const from = `${process.env.REACT_APP_FROM_EMAIL2}`;
    //       if (!from) throw new Error("Missing FROM_EMAIL in environment");

    //       const jwt = getAuthCookie("sessionToken") || "";

    //       await sendTriggerEmails(
    //         "User Registration",
    //         licenseTypes,
    //         params,
    //         dynamicRecipients,
    //         from,
    //         jwt
    //       );
    //     }

    //   } catch (err) {
    //     console.error(err instanceof Error ? err.message : "Unknown error");
    //     toast.error("Failed to add Teammember.");
    //   } finally {
    //     setFormSubmitting(false);
    //   }
    // },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0];
    if (file) {
      formik.setFieldValue("image", file); // Save the file object in Formik
      setPreview(URL.createObjectURL(file)); // Set preview for display
    }
  };

  const uploadFile = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("uploadDir", "profiles");

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${process.env.REACT_APP_API_URL}/images/upload`, true);
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);

      xhr.onload = () => {
        if (xhr.readyState === 4) {
          const response = JSON.parse(xhr.responseText);

          // Construct the full image URL using the base URL and `path` field
          const imageUrl = `${response.path}`;
          resolve(imageUrl);
        } else {
          reject(new Error("Failed to upload file"));
        }
      };

      xhr.onerror = () => reject(new Error("Network error during file upload"));
      xhr.send(formData);
    });
  };

  return (
    <div className="content">
      <div className="col-md-12 d-flex justify-content-center">
        <div className="page-container mt-3 col-md-9">
          <h3 className="m-0 text-center fw-bold addheaders">Add Brokerage Team Member </h3>
        </div>
      </div>
      {/* <h2 className="d-flex justify-content-center">Add Team Member </h2> */}

      <div className="row ms-0">
        <div className="col-md-12 d-flex justify-content-center" id="row-overlap">
          <div className="col-md-9">
            <div className="addheaders-content mt-1 card-bg-fill">
              <div className="p-2 px-4 mt-1">
                <form onSubmit={formik.handleSubmit}>
                  <div className="mt-4 row cust-margin-4">
                    <div>
                      <h5>Personal Info</h5>
                      <hr className="lines" style={{ width: "99px" }} />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">
                        First Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="firstName"
                        name="firstName"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.firstName}
                      />
                      {formik.touched.firstName &&
                        formik.errors.firstName ? (
                        <div className="text-danger">
                          {formik.errors.firstName}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">
                        Last Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="lastName"
                        name="lastName"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.lastName}
                      />
                      {formik.touched.lastName && formik.errors.lastName ? (
                        <div className="text-danger">
                          {formik.errors.lastName}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">
                        Phone Number <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="phone"
                        name="phone"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.phone}
                      />
                      {formik.touched.phone && formik.errors.phone ? (
                        <div className="text-danger">
                          {formik.errors.phone}
                        </div>
                      ) : null}
                    </div>
                    <div>
                      <h5>Account Access</h5>
                      <hr className="lines" style={{ width: "120px" }} />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Email Address <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        id="email"
                        name="email"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.email}
                      />
                      {formik.touched.email && formik.errors.email ? (
                        <div className="text-danger">
                          {formik.errors.email}
                        </div>
                      ) : null}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="form-label">
                        Password <span className="text-danger">*</span>
                      </label>
                      <div className="position-relative auth-pass-inputgroup">
                        <input
                          type={passwordVisible ? "text" : "password"}
                          className="form-control pe-5 password-input"
                          id="password"
                          name="password"
                          onChange={handlePasswordChange}
                          onFocus={handleFocus}
                          onBlur={handleBlur}
                          value={formik.values.password}
                          aria-describedby="password"
                        />
                        <button
                          className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted password-addon material-shadow-none"
                          type="button"
                          id="password-addon"
                          onClick={togglePasswordVisibility}
                        >
                          <i
                            className={
                              passwordVisible
                                ? "ri-eye-off-fill align-middle"
                                : "ri-eye-fill align-middle"
                            }
                          ></i>
                        </button>
                      </div>

                      {formik.touched.password && formik.errors.password && (
                        <div className="text-danger mt-1">{formik.errors.password}</div>
                      )}

                      {showValidationBox && (
                        <div className="bg-light border rounded p-2 mt-2 shadow-sm">
                          <small className="fw-semibold">Password must contain:</small>
                          <ul className="list-unstyled mb-0 mt-2">
                            <li>
                              <i
                                className={
                                  validation.length
                                    ? "ri-check-line text-success"
                                    : "ri-close-line text-danger"
                                }
                              ></i>
                              <span className="ms-2">At least 8 characters</span>
                            </li>
                            <li>
                              <i
                                className={
                                  validation.uppercase
                                    ? "ri-check-line text-success"
                                    : "ri-close-line text-danger"
                                }
                              ></i>
                              <span className="ms-2">At least one uppercase letter</span>
                            </li>
                            <li>
                              <i
                                className={
                                  validation.lowercase
                                    ? "ri-check-line text-success"
                                    : "ri-close-line text-danger"
                                }
                              ></i>
                              <span className="ms-2">At least one lowercase letter</span>
                            </li>
                            <li>
                              <i
                                className={
                                  validation.digit
                                    ? "ri-check-line text-success"
                                    : "ri-close-line text-danger"
                                }
                              ></i>
                              <span className="ms-2">At least one digit</span>
                            </li>
                            <li>
                              <i
                                className={
                                  validation.specialChar
                                    ? "ri-check-line text-success"
                                    : "ri-close-line text-danger"
                                }
                              ></i>
                              <span className="ms-2">
                                At least one special character (e.g., !@#$%^&*)
                              </span>
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>

                    <h5>Profile</h5>
                    <div>
                      <hr className="lines" style={{ width: "50px" }} />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">
                        Photo/Avatar Upload
                      </label>
                      <input
                        type="file"
                        className={`form-control ${formik.touched.image && formik.errors.image
                          ? "is-invalid"
                          : ""
                          }`}
                        id="image"
                        name="image"
                        onChange={handleFileChange}
                        onBlur={formik.handleBlur}
                        accept="image/*"
                      />
                      {formik.touched.image && formik.errors.image ? (
                        <div className="invalid-feedback">
                          {formik.errors.image}
                        </div>
                      ) : null}
                    </div>
                    <div>
                      <h5>Permissions & Assignment</h5>
                      <hr className="lines" style={{ width: "198px" }} />
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label">
                        Role{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-control"
                        id="jobTitle"
                        name="jobTitle"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.jobTitle}
                      >
                        <option value="">- Select -</option>
                        <option value="Manager">Manager</option>
                        <option value="Admin">Admin</option>
                        <option value="VP">VP</option>
                        <option value="Marketing">Marketing</option>
                        <option value="Legal">Legal</option>
                        <option value="Custom">Custom</option>
                      </select>
                      {formik.touched.jobTitle && formik.errors.jobTitle ? (
                        <div className="text-danger">
                          {formik.errors.jobTitle}
                        </div>
                      ) : null}
                    </div>

                    <div className="col-md-3 mb-3">
                      <label className="form-label">
                        Assign Offices{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <Select
                        id="assignedOffices"
                        name="assignedOffices"
                        options={officesOptions}
                        isMulti
                        closeMenuOnSelect={false}
                        value={officesOptions?.filter((option) =>
                          formik.values.assignedOffices.includes(option.value)
                        )}
                        onChange={(selectedOptions) =>
                          formik.setFieldValue(
                            "assignedOffices",
                            selectedOptions
                              ? selectedOptions?.map(
                                (option) => option.value
                              )
                              : []
                          )
                        }
                        onBlur={formik.handleBlur}
                      />

                      {formik.touched.assignedOffices &&
                        formik.errors.assignedOffices ? (
                        <div className="text-danger">
                          {formik.errors.assignedOffices}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label">
                        Permissions/Access Level{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <Select
                        id="permissions"
                        name="permissions"
                        options={permissionsOptions} // The options array
                        isMulti // Enable multiple selection
                        closeMenuOnSelect={false} // Keep the menu open while selecting multiple
                        value={permissionsOptions?.filter((option) =>
                          formik.values.permissions.includes(option.value)
                        )} // Map the selected values to the select component
                        onChange={(permissionsOptions) => {
                          formik.setFieldValue(
                            "permissions",
                            permissionsOptions
                              ? permissionsOptions?.map(
                                (option) => option.value
                              )
                              : []
                          );
                        }}
                        onBlur={formik.handleBlur}
                      />

                      {formik.touched.permissions &&
                        formik.errors.permissions ? (
                        <div className="text-danger">
                          {formik.errors.permissions}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label">
                        Status <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-control"
                        id="statuss"
                        name="statuss"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.statuss}
                      >
                        <option value="">- Select -</option>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                      {formik.touched.statuss &&
                        formik.errors.statuss ? (
                        <div className="text-danger">
                          {formik.errors.statuss}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="row mb-3 rcust-margin-4">
                    <div className="col-md-12 d-flex justify-content-between">
                      <button
                        className="btn btn-outline-secondary br-8 backbutton"
                        type="button"
                        onClick={() => navigate(-1)}
                      >
                        Back
                      </button>
                      <button className="btn btn-primary submit-btn" type="submit">
                        Save
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignageBrokerageAddTeammember;
